import React, { useCallback, useEffect, useMemo, useState } from "react";

/**
 * H10SelfInitPanel — simplified per-tenant H-10 setup view embedded in
 * CompanyHome at /companyHome/<subdomain>. Intentionally lighter than the
 * admin H10ModulePanel:
 *   - no Init/Repair, no Readiness, no Missing Setup table
 *   - no admin Database Manager surface
 *   - no exclude/restore, no Clear Drafts, no bulk admin tools
 *   - one focused job: list candidate injection wells → fill one → submit
 *
 * Backend: calls /api/db_manager.php using the same admin Bearer the admin
 * UI uses. A NEW action `h10_self_init_status` is called first; if the
 * tenant's clients.EnableH10SelfInit !== 'Y' the proxy returns 403 and the
 * panel refuses to render the form. (Defense in depth — the parent
 * CompanyHome.js already hides this tab when the flag is off; the status
 * check makes a direct URL hit and a stale tab equally safe.)
 */

const API_BASE = "/api/db_manager.php";

const FLUID_KEYS = ["SWPct", "NORMPct", "FreshPct", "TotalCO2Pct", "NGPct", "H2SPct", "PolymerPct", "SteamPct", "AirPct", "NitrogenPct", "FracWaterFBPct", "OtherPct"];
const FLUID_EXTRA = ["NORMPct", "FreshPct", "TotalCO2Pct", "AnthropogenicCO2Pct", "NGPct", "H2SPct", "PolymerPct", "SteamPct", "AirPct", "NitrogenPct", "FracWaterFBPct", "OtherPct"];

const MODAL_FIELDS = [
  { k: "WellNumber", label: "Well Number", kind: "text", help: "Well number on the lease." },
  { k: "H10MonthDue", label: "H-10 Due Month (MM)", kind: "text", ph: "09", help: "Annual H-10 due month, 01–12." },
  { k: "H10API", label: "API # (8 digits)", kind: "text", ph: "42xxxxxx", help: "8-digit Texas RRC API number." },
  { k: "UICType", label: "UIC Type", kind: "select", opts: [["", "— select —"], ["1", "1"], ["2", "2"], ["3", "3 — salt-water disposal"], ["4", "4"], ["6", "6"]], help: "UIC well class. 3 = salt-water disposal." },
  { k: "InjThrough", label: "Injection Through", kind: "select", opts: [["", "— select —"], ["1", "Tubing (1)"], ["2", "Casing (2)"]], help: "Whether injection is through tubing (1) or casing (2)." },
  { k: "InjIntervalDepthTop", label: "Interval Top (ft)", kind: "num", help: "Injection interval TOP depth." },
  { k: "InjIntervalDepthBottom", label: "Interval Bottom (ft)", kind: "num", help: "Injection interval BOTTOM depth (must exceed top)." },
  { k: "TbgPackerDepth", label: "Tubing/Packer Depth (ft)", kind: "num", help: "Tubing/packer setting depth." },
  { k: "SWPct", label: "Salt Water %", kind: "num", help: "Salt-water share. All fluid % must total 100." },
];

function rowMissing(r) {
  const m = [];
  const s = (v) => (v == null ? "" : String(v)).trim();
  const pos = (v) => parseInt(s(v), 10) > 0;
  if (s(r.LeaseID) === "") m.push("LeaseID");
  if (s(r.WellNumber) === "") m.push("WellNumber");
  if (!/^(0[1-9]|1[0-2])$/.test(s(r.H10MonthDue))) m.push("H10MonthDue");
  if (!/^\d{8}$/.test(s(r.H10API))) m.push("H10API");
  if (!["1", "2", "3", "4", "6"].includes(s(r.UICType))) m.push("UICType");
  if (!pos(r.InjIntervalDepthTop)) m.push("InjIntervalDepthTop");
  if (!pos(r.InjIntervalDepthBottom)) m.push("InjIntervalDepthBottom");
  if (!pos(r.TbgPackerDepth)) m.push("TbgPackerDepth");
  if (!["1", "2"].includes(s(r.InjThrough))) m.push("InjThrough");
  const sum = FLUID_KEYS.reduce((a, k) => a + (parseFloat(r[k]) || 0), 0);
  if (Math.abs(sum - 100) > 0.01) m.push("FluidPctSum100");
  if ((parseFloat(r.OtherPct) || 0) > 0 && (r.OtherType1 == null || String(r.OtherType1).trim() === "")) m.push("OtherType");
  return m;
}
const rowReady = (r) => r.status !== "Finalized" && rowMissing(r).length === 0;
function effCat(r) {
  if (r.category === "already_configured") return "already_configured";
  if (r.category === "excluded") return "excluded";
  return rowReady(r) ? "ready" : "needs_fields";
}

function rowToSaveObj(r) {
  const o = { LeaseID: r.LeaseID, LeaseName: r.LeaseName };
  MODAL_FIELDS.forEach(({ k }) => { o[k] = r[k]; });
  ["NORMPct", "FreshPct", "TotalCO2Pct", "AnthropogenicCO2Pct", "NGPct", "H2SPct", "PolymerPct", "SteamPct", "AirPct", "NitrogenPct", "FracWaterFBPct", "OtherPct", "InjFluidSourceOthers"].forEach((f) => { if (r[f] != null) o[f] = r[f]; });
  return o;
}

export default function H10SelfInitPanel({ subdomain, theme = "light", companyName }) {
  const isDark = theme === "dark";
  const [status, setStatus] = useState({ checking: true, ok: false, reason: null });
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tab, setTab] = useState("needs_fields");
  const [editRow, setEditRow] = useState(null);

  const card = isDark ? "bg-gray-800 border-gray-700 text-gray-100" : "bg-white border-gray-200 text-gray-800";
  const sub = isDark ? "border-gray-700" : "border-gray-200";
  const btn = "px-3 py-1.5 rounded text-sm font-medium border transition disabled:opacity-50 disabled:cursor-not-allowed";
  const btnP = isDark ? "bg-blue-600 border-blue-500 text-white hover:bg-blue-500" : "bg-blue-600 border-blue-600 text-white hover:bg-blue-700";
  const btnN = isDark ? "bg-gray-700 border-gray-600 text-gray-100 hover:bg-gray-600" : "bg-gray-100 border-gray-300 text-gray-800 hover:bg-gray-200";
  const inpCls = (bad) => `w-full px-2 py-1 text-sm rounded border ${bad ? "border-red-500 ring-1 ring-red-500" : isDark ? "border-gray-600" : "border-gray-300"} ${isDark ? "bg-gray-900 text-gray-100" : "bg-white"}`;

  const callApi = useCallback(
    async (action, payload = {}) => {
      const tok = sessionStorage.getItem("og_access_token") || "";
      const resp = await fetch(API_BASE, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ action, init_subdomain: subdomain, ...payload }),
      });
      const data = await resp.json().catch(() => ({ ok: false, message: `HTTP ${resp.status}` }));
      if (!resp.ok || !data.ok) throw Object.assign(new Error(data.message || `HTTP ${resp.status}`), { data, status: resp.status });
      return data;
    },
    [subdomain]
  );

  const refresh = useCallback(async () => {
    setBusy("preview"); setError("");
    try {
      const resp = await callApi("h10_candidates_preview");
      const res = resp?.data?.result;
      setRows((res?.candidates || []).map((c) => ({ ...c })));
    } catch (e) {
      setError(e.message);
    } finally { setBusy(""); }
  }, [callApi]);

  // Status check first — confirms the tenant flag is ON; if not we refuse to render the form.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await callApi("h10_self_init_status");
        if (cancelled) return;
        const enabled = !!(resp?.data?.enabled);
        setStatus({ checking: false, ok: enabled, reason: enabled ? null : (resp?.data?.reason || "Self-init not enabled for this tenant.") });
        if (enabled) refresh();
      } catch (e) {
        if (!cancelled) setStatus({ checking: false, ok: false, reason: e.message || "Status check failed." });
      }
    })();
    return () => { cancelled = true; };
  }, [callApi, refresh]);

  const counts = useMemo(() => {
    const c = { ready: 0, needs_fields: 0, already_configured: 0, excluded: 0 };
    (rows || []).forEach((r) => { c[effCat(r)] = (c[effCat(r)] || 0) + 1; });
    return c;
  }, [rows]);
  const tabRows = (rows || []).filter((r) => {
    if (tab === "already_configured") return effCat(r) === "already_configured";
    // "Needs Setup" includes both needs_fields and freshly-ready (not yet submitted) — excluded leases are not shown in self-init.
    return effCat(r) === "needs_fields" || effCat(r) === "ready";
  });

  const editMissing = editRow ? rowMissing(editRow) : [];
  const editFluidTotal = editRow ? FLUID_KEYS.reduce((a, k) => a + (parseFloat(editRow[k]) || 0), 0) : 0;
  const setEdit = (k, v) => setEditRow((p) => ({ ...p, [k]: v }));

  const openEditor = (r) => {
    const seed = { ...r };
    FLUID_KEYS.concat(["AnthropogenicCO2Pct"]).forEach((k) => { if (seed[k] == null) seed[k] = (k === "SWPct" ? 100 : 0); });
    setEditRow(seed);
    setError(""); setNotice("");
  };

  const doSave = useCallback(async () => {
    if (!editRow) return;
    setBusy("save"); setError("");
    try {
      await callApi("h10_candidates_save_draft", { candidates: [rowToSaveObj(editRow)] });
      setNotice(`Saved ${editRow.LeaseID} — you can come back to finish later.`);
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [editRow, callApi, refresh]);

  const doSubmit = useCallback(async () => {
    if (!editRow) return;
    const miss = rowMissing(editRow);
    if (miss.length) { setError(`Some required fields are missing: ${miss.join(", ")}.`); return; }
    if (!window.confirm(`Submit H-10 setup for ${editRow.LeaseID}? It writes a regulatory record and cannot overwrite a well that is already set up.`)) return;
    setBusy("submit"); setError("");
    try {
      // Save the draft first (assigns a candidate_id and runs server-side validation).
      await callApi("h10_candidates_save_draft", { candidates: [rowToSaveObj(editRow)] });
      // Re-preview to resolve the candidate_id for this lease.
      const prev = await callApi("h10_candidates_preview");
      const prevCands = prev?.data?.result?.candidates || [];
      const match = prevCands.find((c) => c.LeaseID === editRow.LeaseID && c.can_finalize && c.candidate_id);
      if (!match) {
        setError("This lease did not validate server-side. Please re-check the required fields.");
        return;
      }
      const fin = await callApi("h10_candidates_finalize", { candidate_ids: [match.candidate_id] });
      const res = fin?.data?.result;
      const executed = res?.executed ?? res?.finalized ?? 0;
      const skipped = res?.skipped ?? 0;
      if (executed > 0) {
        setNotice(`Submitted — H-10 setup for ${editRow.LeaseID} is now on file.`);
        setEditRow(null);
      } else if (skipped > 0) {
        setNotice("This lease was already configured by someone else; no changes made.");
        setEditRow(null);
      } else {
        setError((res?.errors || []).map((e) => e.reason).join("; ") || "Submit could not complete.");
      }
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [editRow, callApi, refresh]);

  // ── render ──────────────────────────────────────────────────────────────
  if (status.checking) {
    return <div className={`rounded-lg border p-4 ${card}`}>Checking H-10 self-setup access…</div>;
  }
  if (!status.ok) {
    return (
      <div className={`rounded-lg border p-4 ${card}`}>
        <h2 className="text-xl font-semibold mb-1">H-10 Well Setup</h2>
        <p className="text-sm opacity-70">H-10 self-setup is not enabled for this company.{status.reason ? ` (${status.reason})` : ""}</p>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border p-4 ${card}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold m-0">H-10 Well Setup{companyName ? <span className="text-xs font-normal opacity-70 ml-2">— {companyName}</span> : null}</h2>
          <p className="text-sm opacity-70 m-0">Fill out injection well setup details for H-10 reporting.</p>
        </div>
        <button className={`${btn} ${btnN}`} disabled={!!busy} onClick={refresh}>{busy === "preview" ? "…" : "Refresh"}</button>
      </div>

      {error ? <div className="text-sm rounded p-2 mb-2" style={{ background: "#c0392b22", color: "#c0392b" }}>{error}</div> : null}
      {notice ? <div className="text-sm rounded p-2 mb-2" style={{ background: "#1e8a4922", color: "#1e8449" }}>{notice}</div> : null}

      <div className="text-xs opacity-70 mb-2">
        {counts.needs_fields + counts.ready} lease(s) need setup · {counts.already_configured} already set up
      </div>

      <div className="flex gap-1 mb-2">
        <button className={`px-2 py-1 text-xs rounded border ${tab === "needs_fields" ? "font-bold" : "opacity-70"} ${sub}`} style={tab === "needs_fields" ? { background: "#b9770e", color: "#fff", borderColor: "#b9770e" } : {}} onClick={() => setTab("needs_fields")}>Needs Setup ({counts.needs_fields + counts.ready})</button>
        <button className={`px-2 py-1 text-xs rounded border ${tab === "already_configured" ? "font-bold" : "opacity-70"} ${sub}`} style={tab === "already_configured" ? { background: "#566573", color: "#fff", borderColor: "#566573" } : {}} onClick={() => setTab("already_configured")}>Already Set Up ({counts.already_configured})</button>
      </div>

      {rows ? (
        tabRows.length ? (
          <div className="overflow-auto" style={{ maxHeight: 360 }}>
            <table className="w-full text-xs border-collapse">
              <thead><tr className={isDark ? "text-gray-300" : "text-gray-600"}>
                <th className={`text-left p-1 border-b ${sub}`}>Lease</th>
                <th className={`text-left p-1 border-b ${sub}`}>Well #</th>
                <th className={`text-left p-1 border-b ${sub}`}>Status</th>
                <th className={`text-left p-1 border-b ${sub}`}>{tab === "already_configured" ? "Due" : "What's missing"}</th>
                <th className={`text-left p-1 border-b ${sub}`}>Action</th>
              </tr></thead>
              <tbody>
                {tabRows.map((r) => {
                  const cat = effCat(r);
                  const miss = rowMissing(r);
                  const statusLabel = cat === "ready" ? "Ready" : cat === "needs_fields" ? "Needs Setup" : "Configured";
                  const statusBg = cat === "ready" ? "#1e8449" : cat === "needs_fields" ? "#b9770e" : "#566573";
                  return (
                    <tr key={r.LeaseID}>
                      <td className="p-1 align-top whitespace-nowrap"><span className="font-mono">{r.LeaseID}</span><div className="opacity-60">{r.LeaseName}</div></td>
                      <td className="p-1 align-top">{r.WellNumber || "—"}</td>
                      <td className="p-1 align-top"><span className="px-1 rounded text-white" style={{ background: statusBg }}>{statusLabel}</span></td>
                      <td className="p-1 align-top opacity-80">{cat === "already_configured" ? (r.H10MonthDue || "—") : (miss.length ? miss.join(", ") : <span style={{ color: "#1e8449" }}>none ✓</span>)}</td>
                      <td className="p-1 align-top">{(cat === "needs_fields" || cat === "ready") ? <button className={`${btn} ${btnP} !px-2 !py-0.5 !text-xs`} disabled={!!busy} onClick={() => openEditor(r)}>{cat === "ready" ? "Edit" : "Fill Out"}</button> : <span className="opacity-50">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-xs opacity-70 p-2">{tab === "already_configured" ? "Nothing here yet." : "Nothing to set up — you're done!"}</div>
        )
      ) : (
        <div className="text-xs opacity-70">Loading…</div>
      )}

      {editRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => !busy && setEditRow(null)}>
          <div className={`w-full max-w-2xl max-h-[90vh] overflow-auto rounded-lg border p-4 ${card}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-base font-semibold">Fill out H-10 setup</h4>
              <button className={`${btn} ${btnN} !py-0.5`} disabled={!!busy} onClick={() => setEditRow(null)}>Close</button>
            </div>
            <div className="text-xs mb-2">
              <span className="font-mono">{editRow.LeaseID}</span> <span className="opacity-60">{editRow.LeaseName}</span>
              <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-semibold text-white" style={{ background: editMissing.length ? "#b9770e" : "#1e8449" }}>
                {editMissing.length ? `${editMissing.length} field(s) missing` : "Ready ✓"}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {MODAL_FIELDS.map((f) => {
                const bad = editMissing.includes(f.k) || (f.k === "SWPct" && editMissing.includes("FluidPctSum100"));
                return (
                  <div key={f.k}>
                    <label className="text-xs font-semibold flex items-center gap-1">{f.label}{bad ? <span style={{ color: "#c0392b" }}>*</span> : null}</label>
                    {f.kind === "select" ? (
                      <select className={inpCls(bad)} value={editRow[f.k] ?? ""} onChange={(e) => setEdit(f.k, e.target.value)}>
                        {f.opts.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                      </select>
                    ) : (
                      <input className={inpCls(bad)} inputMode={f.kind === "num" ? "numeric" : undefined} placeholder={f.ph || ""} value={editRow[f.k] ?? ""} onChange={(e) => setEdit(f.k, e.target.value)} />
                    )}
                    <div className="text-[10px] opacity-60 mt-0.5">{f.help}</div>
                  </div>
                );
              })}
            </div>
            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-semibold">
                Advanced fluid composition — total {editFluidTotal}%{" "}
                <span style={{ color: Math.abs(editFluidTotal - 100) < 0.01 ? "#1e8449" : "#c0392b" }}>{Math.abs(editFluidTotal - 100) < 0.01 ? "✓" : "(must total 100)"}</span>
              </summary>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {FLUID_EXTRA.map((k) => (
                  <div key={k}>
                    <label className="text-[10px] font-semibold">{k.replace("Pct", " %")}</label>
                    <input className={inpCls(false)} inputMode="numeric" value={editRow[k] ?? 0} onChange={(e) => setEdit(k, e.target.value)} />
                  </div>
                ))}
              </div>
            </details>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button className={`${btn} ${btnN}`} disabled={!!busy} onClick={() => setEditRow(null)}>Cancel</button>
              <button className={`${btn} ${btnN}`} disabled={!!busy} onClick={doSave}>{busy === "save" ? "…" : "Save for later"}</button>
              <button className={`${btn} ${btnP}`} disabled={!!busy || editMissing.length > 0} onClick={doSubmit}>{busy === "submit" ? "…" : "Submit H-10 setup"}</button>
            </div>
            <p className="text-[10px] opacity-60 mt-2">Save keeps your draft. Submit writes the validated values into the H-10 record. You cannot overwrite a well that is already set up.</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
